import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type QuizBankFacetsRes } from '@/api/quizBank';

import {
  type QuizBankAppliedFallback,
  type QuizBankFallbackAxis,
  type QuizBankFallbackState,
  type QuizBankFilterState,
} from '../types';
import { toQuizBankSearchFallback } from '../utils';

interface UseQuizBankSearchFallbackParams {
  filter: QuizBankFilterState;
  changeFilter: (next: QuizBankFilterState) => void;
  facets: QuizBankFacetsRes | null | undefined;
  totalCount: number | undefined;
  status: 'loading' | 'success' | 'error';
}

interface FallbackGuard {
  query: string;
  applied: QuizBankFallbackAxis[];
  locked: boolean;
}

const freshGuard = (query: string): FallbackGuard => ({
  query,
  applied: [],
  locked: false,
});

/**
 * 키워드가 현재 필터로 0건이면 facet 에 건수가 남은 축을 단원 → 유형 → 학년 순으로 하나 푼다
 * (`toQuizBankSearchFallback`). placeholder(이전 필터 결과)는 status 가 loading 이라 걸리지 않는다.
 *
 * 수명은 검색어 하나다 — 축마다 한 번만 풀고, 사용자가 필터를 손대면 그 검색어의 완화는 끝난다.
 * 풀린 축을 다시 좁혀 0건이 나와도 되돌리지 않아야 사용자의 선택이 이긴다.
 *
 * @return `onUserFilterChange` 는 뷰에 물릴 필터 변경 핸들러 — 사용자 변경을 자동 완화와 가르려면
 *   이쪽을 써야 한다. `fallback` 은 화면·트래킹이 읽는 완화 상태다.
 */
export const useQuizBankSearchFallback = ({
  filter,
  changeFilter,
  facets,
  totalCount,
  status,
}: UseQuizBankSearchFallbackParams) => {
  // 렌더 중에 `pending` 을 순수하게 셈해야 해서 ref 가 아니라 state 다. 갱신은 언제나
  // `changeFilter` 와 같은 배치라 추가 렌더를 만들지 않는다.
  const [guard, setGuard] = useState<FallbackGuard>(() =>
    freshGuard(filter.query),
  );
  const [applied, setApplied] = useState<QuizBankAppliedFallback | null>(null);

  // StrictMode 의 마운트 이중 실행을 동기적으로 끊는다 — state 갱신은 다음 렌더에야 보여서
  // 이것 없이는 두 번째 실행이 같은 축을 또 푼다.
  const appliedLatch = useRef<string | null>(null);

  const onUserFilterChange = useCallback(
    (next: QuizBankFilterState) => {
      setGuard(current => {
        if (next.query === current.query) return { ...current, locked: true };
        appliedLatch.current = null;
        return freshGuard(next.query);
      });
      changeFilter(next);
    },
    [changeFilter],
  );

  const pendingFallback = useMemo(() => {
    if (status !== 'success' || totalCount !== 0 || !facets) return null;
    if (guard.locked || guard.query !== filter.query) return null;
    const next = toQuizBankSearchFallback(filter, facets);
    if (!next || guard.applied.includes(next.axis)) return null;
    return next;
  }, [status, totalCount, facets, guard, filter]);

  useEffect(() => {
    if (!pendingFallback) return;
    const { axis, patch } = pendingFallback;
    const latchKey = `${filter.query}|${axis}`;
    if (appliedLatch.current === latchKey) return;
    appliedLatch.current = latchKey;

    const beforeGradeId = filter.gradeId ?? '';
    setApplied({
      axis,
      index: guard.applied.length + 1,
      query: filter.query,
      beforeGradeId,
      afterGradeId: patch.gradeId ?? beforeGradeId,
      clearedUnitCount: patch.unitIds ? filter.unitIds.length : 0,
      clearedTypeCount: patch.types ? filter.types.length : 0,
    });
    setGuard(current => ({ ...current, applied: [...current.applied, axis] }));
    changeFilter({ ...filter, ...patch });
  }, [pendingFallback, guard.applied.length, filter, changeFilter]);

  const fallback: QuizBankFallbackState = useMemo(
    () => ({
      count: guard.applied.length,
      pending: pendingFallback !== null,
      applied,
    }),
    [guard.applied.length, pendingFallback, applied],
  );

  return { onUserFilterChange, fallback };
};
